import React, { useState, useEffect } from "react";
import * as adminService from "@/shared/api/services/adminService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/components/ui/card";
import {
  ArrowLeft,
  Save,
  Shield,
  CheckCircle2,
  X,
} from "lucide-react";
import { Checkbox } from "@/shared/components/ui/checkbox";

// Predefined permission options (labels translated via permission.${key} in the UI)
const PERMISSION_GROUPS = {
  merchants: [
    { key: "merchants.view" },
    { key: "merchants.create" },
    { key: "merchants.edit" },
    { key: "merchants.delete" },
  ],
  engines: [
    { key: "engines.view" },
    { key: "engines.create" },
    { key: "engines.edit" },
    { key: "engines.delete" },
  ],
  resellers: [
    { key: "resellers.view" },
    { key: "resellers.create" },
    { key: "resellers.edit" },
    { key: "resellers.delete" },
  ],
  transactions: [
    { key: "transactions.view" },
    { key: "transactions.create" },
    { key: "transactions.edit" },
  ],
  staff: [
    { key: "staff.view" },
    { key: "staff.create" },
    { key: "staff.edit" },
    { key: "staff.delete" },
  ],
  settings: [
    { key: "settings.view" },
    { key: "settings.edit" },
  ],
  roles: [
    { key: "roles.view" },
    { key: "roles.create" },
    { key: "roles.edit" },
    { key: "roles.delete" },
  ],
};

export default function AdminRoleForm() {
  const { t } = useLanguage();
  const urlParams = new URLSearchParams(window.location.search);
  const roleId = urlParams.get("id");
  const isEdit = !!roleId;

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    panelType: "ADMIN",
    permissions: [],
  });

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({ name: false, description: false, permissions: false });

  const { data: existingRole } = useQuery({
    queryKey: ["role", roleId],
    queryFn: () => {
      // Get role from available roles
      return adminService.getAvailableRoles().then(roles =>
        roles.find(r => r.id === roleId)
      );
    },
    enabled: isEdit,
  });

  useEffect(() => {
    if (existingRole) {
      setFormData(prev => ({
        ...prev,
        name: existingRole.name,
        description: existingRole.description || "",
        panelType: existingRole.panelType,
        permissions: existingRole.permissions || [],
      }));
    }
  }, [existingRole]);

  const createMutation = useMutation({
    mutationFn: (data) => adminService.createStaffRole(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["available-roles"]);
      toast.success("Role created successfully");
      navigate(createPageUrl("AdminRoles"));
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to create role");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }) => adminService.updateStaffRole(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries(["available-roles"]);
      queryClient.invalidateQueries(["role", roleId]);
      toast.success("Role updated successfully");
      navigate(createPageUrl("AdminRoles"));
    },
    onError: (err) => {
      toast.error(err?.message || "Failed to update role");
    },
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handlePermissionToggle = (permission) => {
    setFormData((prev) => {
      const permissions = prev.permissions || [];
      if (permissions.includes(permission)) {
        return { ...prev, permissions: permissions.filter(p => p !== permission) };
      } else {
        return { ...prev, permissions: [...permissions, permission] };
      }
    });
    // Clear the permissions error as soon as user selects any permission
    if (errors.permissions) {
      setErrors((prev) => ({ ...prev, permissions: false }));
    }
  };

  const handleSelectAll = (group) => {
    const groupPermissions = PERMISSION_GROUPS[group].map(p => p.key);
    setFormData((prev) => {
      const current = prev.permissions || [];
      const allSelected = groupPermissions.every(p => current.includes(p));

      if (allSelected) {
        // Deselect all in group
        return { ...prev, permissions: current.filter(p => !groupPermissions.includes(p)) };
      } else {
        // Select all in group
        const newPermissions = [...new Set([...current, ...groupPermissions])];
        return { ...prev, permissions: newPermissions };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields first
    const newErrors = {
      name: !formData.name.trim(),
      description: !formData.description.trim(),
      permissions: formData.permissions.length === 0,
    };
    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some(Boolean);
    if (hasErrors) {
      if (newErrors.permissions) {
        toast.error("At least one permission must be selected");
      }
      return;
    }

    setSaving(true);

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({
          id: roleId,
          input: {
            name: formData.name,
            description: formData.description,
            permissions: formData.permissions,
          },
        });
      } else {
        await createMutation.mutateAsync({
          name: formData.name,
          description: formData.description,
          panelType: formData.panelType,
          permissions: formData.permissions,
          isSystem: false,
        });
      }
    } catch (error) {
      console.error("Failed to save role:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to={createPageUrl("AdminRoles")}>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEdit ? t("editRole") : t("createRole")}
          </h1>
          <p className="text-slate-500">
            {isEdit ? t("updateRoleDetailsAndPermissions") : t("defineNewRoleWithPermissions")}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6">
          {/* General Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t("generalInformation")}</CardTitle>
              <CardDescription>{t("basicDetailsForRole")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t("roleNameRequired")}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder={t("merchantManagerPlaceholder")}
                  disabled={isEdit && existingRole?.isSystem}
                  className={errors.name ? "border-red-500 focus-visible:ring-red-500" : ""}
                />
                {errors.name && (
                  <p className="text-xs text-red-500 mt-1">Role name is required</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{t("description")}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder={t("describeWhatRoleCanDo")}
                  rows={3}
                  disabled={isEdit && existingRole?.isSystem}
                  className={errors.description ? "border-red-500 focus-visible:ring-red-500" : ""}
                />
                {errors.description && (
                  <p className="text-xs text-red-500 mt-1">Description is required</p>
                )}
              </div>

              {!isEdit && (
                <div className="space-y-2">
                  <Label htmlFor="panelType">{t("panelTypeRequired")}</Label>
                  <div className="text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
                    {t("roleCreatedForAdminPanel")} <strong>{t("adminPanel")}</strong>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Permissions */}
          <Card className={errors.permissions ? "border-red-500 ring-1 ring-red-500" : ""}>
            <CardHeader>
              <CardTitle>{t("permissions")}</CardTitle>
              <CardDescription>{t("selectActionsRoleCanPerform")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(PERMISSION_GROUPS).map(([group, permissions]) => {
                const groupPermissions = permissions.map((p) => p.key)
                const allSelected = groupPermissions.every((p) => formData.permissions.includes(p))
                // const someSelected = groupPermissions.some((p) => formData.permissions.includes(p)) // currently unused

                return (
                  <div key={group} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">
                        {t(group)}
                      </Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSelectAll(group)}
                        disabled={isEdit && existingRole?.isSystem}
                      >
                        {allSelected ? t("deselectAll") : t("selectAll")}
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-4">
                      {permissions.map((permission) => (
                        <div
                          key={permission.key}
                          className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-50"
                        >
                          <Checkbox
                            id={`perm-${permission.key}`}
                            checked={formData.permissions.includes(permission.key)}
                            onCheckedChange={() => handlePermissionToggle(permission.key)}
                            disabled={isEdit && existingRole?.isSystem}
                          />
                          <Label
                            htmlFor={`perm-${permission.key}`}
                            className="text-sm font-normal cursor-pointer flex-1"
                          >
                            {t(`permission.${permission.key}`)}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}

              {formData.permissions.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>{t("noPermissionsSelected")}</p>
                </div>
              )}

              {formData.permissions.length > 0 && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        {formData.permissions.length === 1
                          ? t("permissionSelected")
                          : t("permissionsSelected", { count: formData.permissions.length })}
                      </p>
                      <p className="text-xs text-blue-700 mt-1">{t("staffWithRoleActions")}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Link to={createPageUrl("AdminRoles")}>
              <Button type="button" variant="outline">
                {t("cancel")}
              </Button>
            </Link>

            <Button
              type="submit"
              disabled={saving || (isEdit && existingRole?.isSystem)}
              className="gap-2"
            >
              {saving ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <Save className="h-4 w-4" />
                  </motion.div>
                  {t("saving")}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isEdit ? t("updateRole") : t("createRoleAction")}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}




